import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { ReportIcon, TrashIcon } from "@roman/shared/components/Icons";
import { useDeleteComment } from "@roman/shared/hooks/PredictionMarket/useDeleteComment";
import { useAuthStore } from "@roman/shared/store/auth.store";
import type { PredictionMarketComment } from "@roman/shared/types/Comment";
import { useState } from "react";

type CommentMenuBottomSheetProps = {
  open: boolean;
  setOpen: (value: boolean) => void;
  comment: PredictionMarketComment;
  onReport?: () => void;
  onDeleteSuccess?: () => void;
  onStartInlineEdit?: () => void;
};

export const CommentMenuBottomSheet = ({
  open,
  setOpen,
  comment,
  onReport,
  onDeleteSuccess,
}: CommentMenuBottomSheetProps) => {
  const user = useAuthStore((state) => state.user);
  const isOwnComment = user?.id === comment.authorId;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { deleteComment, isMutating: isDeleting } = useDeleteComment({
    onSuccess: () => {
      onDeleteSuccess?.();
      setOpen(false);
      setShowDeleteConfirm(false);
    },
    onError: (error) => {
      console.error("Failed to delete comment:", error);
      // TODO: Show error toast
    },
  });

  const handleReport = () => {
    onReport?.();
    setOpen(false);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    deleteComment({
      predictionMarketId: comment.predictionMarketId,
      commentId: comment.id,
    });
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    handleOpenChange(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setShowDeleteConfirm(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange} repositionInputs={false}>
      <DrawerContent
        className="mx-auto flex flex-col justify-center md:w-2/3 lg:w-1/2 border-none"
        dragClassName="h-1 mb-4 bg-disabled/70"
      >
        <div className="flex flex-col justify-center w-full border-none px-[32px]">
          {!showDeleteConfirm ? (
            // Menu content
            <div className="animate-in fade-in duration-300">
              <div className="text-left font-bold text-[#5C5C5C] text-[14px] px-[8px] h-[20px]">コメント</div>

              <div className="mt-[16px]">
                {isOwnComment ? (
                  <div className="mb-[46px]">
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-left h-[40px] px-[8px] text-base font-medium text-red-500 md:hover:bg-gray flex items-center gap-3 mt-[14px]"
                      onClick={handleDelete}
                    >
                      <TrashIcon />
                      削除
                    </Button>
                  </div>
                ) : (
                  <div className="mb-[100px]">
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-left h-12 px-[8px] text-base font-medium text-red-500 md:hover:bg-red-50 flex items-center gap-3"
                      onClick={handleReport}
                    >
                      <ReportIcon />
                      報告
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : showDeleteConfirm ? (
            // Delete confirmation content with expand animation
            <div className="animate-in fade-in zoom-in-95 duration-300 ease-out pt-[20px] pb-[31px]">
              <div className="text-center mb-6">
                <h2 className="text-lg font-bold text-black mb-2">コメントを削除しますか？</h2>
                {/* Fixed typo: "lineHeight" should be "leading" in Tailwind CSS */}
                <p className="text-dark-gray font-medium text-[14px] leading-[1.4]">作成した内容は保存されません。</p>
              </div>

              <div className="flex flex-col gap-3">
                <PrimaryButton
                  content={isDeleting ? "削除中..." : "削除する"}
                  onClick={handleDeleteConfirm}
                  className="w-full h-[48px] bg-gradient-primary"
                  disabled={isDeleting}
                />

                <Button
                  variant="ghost"
                  className="text-[#176BF2] underline font-medium md:hover:bg-transparent"
                  onClick={handleDeleteCancel}
                >
                  閉じる
                </Button>
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};
